import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface MinimaxHailuo23FastI2vParams {
  /**
   * 输入图像URL或Base64编码（data:image/jpeg;base64,xxx格式）
   */
  first_frame_image?: string;
  /**
   * 视频生成提示词，可包含摄像机运动指令如[Pan left]、[Zoom in]等
   */
  prompt?: string;
  /**
   * 视频时长（秒），默认6秒
   * @default 6
   */
  duration?: number;
  /**
   * 视频分辨率，默认为768P
   * @default "768P"
   * @values 512P | 768P | 1080P
   */
  resolution?: '512P' | '768P' | '1080P';
  /**
   * 是否启用提示词优化器，默认为true
   * @default true
   */
  prompt_optimizer?: boolean;
  /**
   * 是否启用快速预处理，默认为false
   * @default false
   */
  fast_pretreatment?: boolean;
}

export interface MinimaxHailuo23FastI2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * minimax_hailuo_23_fast_i2v
 *
 * MiniMax Hailuo-2.3-Fast Image-to-Video Generator
 * 
 * Image-to-video generation implementation for MiniMax Hailuo-2.3-Fast model with camera control support.
 * 
 * Model: MiniMax-Hailuo-2.3-Fast
 * Features: 图像生成视频 (Image-to-Video) - 快速模式
 * Duration: 6秒（默认）
 * Resolution: 512P, 768P (默认), 1080P
 * Camera Control: 支持摄像机运动控制（Truck, Pan, Push, Pedestal, Tilt, Zoom, Shake, Tracking, Static）
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, minimaxHailuo23FastI2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await minimaxHailuo23FastI2v({
 *   // 参数见 MinimaxHailuo23FastI2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MinimaxHailuo23FastI2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function minimaxHailuo23FastI2v(
  params: MinimaxHailuo23FastI2vParams,
): Promise<MinimaxHailuo23FastI2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'minimax_hailuo_23_fast_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MinimaxHailuo23FastI2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
